import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { usePendo } from "../../../../analytics/hooks/use-pendo-watch";
import { LinkForm } from "../../components/link-form/link-form";
import { useLexicalContext } from "../../lexical-context";
import {
  INSERT_LINK_COMMAND,
  INSERT_SURVEY_LINK_COMMAND,
  REMOVE_LINK_COMMAND,
} from "../../types/commands";
import { $multipleElementsSelected } from "../../utils/multiple-elements-selected";

import type { Link } from "../../components/link-form/types";
import type { FC } from "react";

// SMS links have no editable link text: the chip shows a fixed label for the chosen
// destination (mirroring the radio labels) rather than the URL or a custom string.
const SMS_LINK_LABELS: Partial<Record<NonNullable<Link["type"]>, string>> = {
  link_url: "url",
  link_lib: "content_library",
  link_survey: "survey_link",
  link_email: "email_communication",
};

export const LinkModalPlugin: FC<{ disabled: boolean }> = ({ disabled }) => {
  const {
    filterTags,
    format,
    editorModal: { handleClose, open, data },
  } = useLexicalContext();

  const [forceDisabled, setForceDisabled] = useState(false);

  const [editor] = useLexicalComposerContext();
  const pendo = usePendo();

  const onConfirm = useCallback(
    ({ text: title, type, url }: Link): void => {
      const isSms = format === "sms";

      // SMS is plain text: an inserted link must render as a survey-link chip so that
      // transformTwilio serialises it to its URL (a regular link node would drop the URL).
      if (
        isSms ||
        type === "link_survey" ||
        (data?.type === "link" && data.data.type === "survey_link")
      ) {
        editor.dispatchCommand(INSERT_SURVEY_LINK_COMMAND, {
          // SMS chips display a fixed destination label (e.g. "URL") instead of the URL or
          // a custom string; fall back to "URL" for any unexpected type.
          title: isSms ? ((type && SMS_LINK_LABELS[type]) ?? "url") : title,
          url,
        });
      } else {
        editor.dispatchCommand(INSERT_LINK_COMMAND, { title, url });
      }

      if (isSms) {
        pendo?.track("sms_link_inserted", { link_type: type ?? null });
      }

      handleClose();
    },
    [editor, handleClose, data, format, pendo],
  );

  const onRemove = useCallback(() => {
    editor.dispatchCommand(REMOVE_LINK_COMMAND, null);
    handleClose();
  }, [editor, handleClose]);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          setForceDisabled($multipleElementsSelected());

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  if (!open || !data || data.type !== "link") return null;

  return (
    <LinkForm
      onRemove={onRemove}
      onCancel={handleClose}
      onConfirm={onConfirm}
      disabled={disabled || forceDisabled}
      filterTags={filterTags}
      data={data.data}
    />
  );
};
